import { useMemo, useState } from "react";
import Breadcrumb from "@/components/base/Breadcrumb";
import FilterChips from "@/components/base/FilterChips";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import ContentCard from "@/components/feature/ContentCard";
import { characters } from "@/mocks/characters";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import type { ContentCategory, UnifiedContent } from "@/types/content";

const PAGE_SIZE = 6;
const ROLES = ["主角", "配角", "反派"];

export default function CharactersCategory() {
  useDocumentTitle({
    title: "角色介绍 - 我妻子的一切",
    description:
      "《我妻子的一切》角色介绍页，收录主要角色的身份定位、背景故事与性格分析，帮助你理清剧中人物关系与动机。",
    keywords: "我妻子的一切角色,演员表,人物介绍,角色分析",
  });

  const [role, setRole] = useState("");
  const [page, setPage] = useState(1);

  const filtered = useMemo(
    () => characters.filter((item) => !role || item.role === role),
    [role],
  );

  const unified: UnifiedContent[] = filtered.map((item) => ({
    id: item.id,
    category: "characters" as ContentCategory,
    title: item.name,
    summary: item.summary,
    cover: item.avatar,
    date: "",
    tags: [item.role, item.actor],
  }));

  const totalPages = Math.max(1, Math.ceil(unified.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = unified.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const reset = () => {
    setRole("");
    setPage(1);
  };

  return (
    <div className="w-full px-4 py-8 md:px-8 md:py-12">
      <Breadcrumb items={[{ label: "角色介绍" }]} />

      <header className="mt-5 max-w-3xl">
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-accent-600">Character Files</p>
        <h1 className="mt-2 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
          角色介绍
        </h1>
        <p className="mt-3 text-sm leading-7 text-foreground-600 md:text-base">
          这里整理了《我妻子的一切》中主要角色的档案：从身份定位、背景经历到性格特征，帮助你理解每个人物在故事里的位置与变化。
        </p>
      </header>

      <div className="mt-8 rounded-lg border border-background-200 bg-background-100 p-4 md:p-5">
        <FilterChips
          label="定位"
          options={ROLES}
          value={role}
          onChange={(v) => {
            setRole(v);
            setPage(1);
          }}
          allLabel="全部角色"
        />
      </div>

      <div className="mt-6 flex items-center justify-between">
        <p className="text-sm text-foreground-500">
          共 <span className="font-medium text-primary-700">{unified.length}</span> 位角色
        </p>
        {role && (
          <button
            type="button"
            onClick={reset}
            className="inline-flex items-center gap-1 whitespace-nowrap text-sm text-foreground-500 transition-colors hover:text-primary-700 cursor-pointer"
          >
            <i className="ri-refresh-line" />
            重置筛选
          </button>
        )}
      </div>

      {pageItems.length > 0 ? (
        <>
          <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {pageItems.map((item) => (
              <ContentCard key={item.id} item={item} />
            ))}
          </div>
          <Pagination current={currentPage} total={totalPages} onChange={setPage} />
        </>
      ) : (
        <div className="mt-5">
          <EmptyState
            icon="ri-user-search-line"
            title="暂无匹配的角色"
            description="当前筛选条件下没有找到角色，试试切换到其他角色定位。"
            actionLabel="重置筛选"
            onAction={reset}
          />
        </div>
      )}
    </div>
  );
}